import { Link, useParams } from 'react-router-dom';
import { Card, CardTitle, CardDescription } from '@/components/ui/card';
import { Tag } from '@/components/common/Tag';
import { ScoreTag } from '@/components/common/ScoreTag';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { useEntity, useEntityNews } from '@/lib/queries';
import { fmtLocalDate } from '@/lib/format';
import type { NewsItemDto } from '@/types/dto';

export default function EntityDetail() {
  const { id } = useParams();
  const entity = useEntity(id);
  const news = useEntityNews(id);

  if (entity.isLoading) return <LoadingSkeleton lines={5} />;
  if (entity.error) return <ErrorState message={(entity.error as Error).message} />;
  if (!entity.data) return null;

  const e = entity.data;
  const tierVariant = e.tier === 1 ? 'tier-1' : e.tier === 2 ? 'tier-2' : 'tier-3';

  return (
    <>
      <Card>
        <div className="flex items-center gap-3 mb-3">
          <Link to="/hmhwatch/entities" className="text-accent hover:underline text-sm">← Entities</Link>
        </div>
        <CardTitle className="text-3xl">{e.name}</CardTitle>
        <div className="flex items-center gap-3 mt-3 flex-wrap">
          <Tag variant={tierVariant}>Tier {e.tier}</Tag>
          <span className="text-muted text-sm">· {e.category}</span>
        </div>
        {e.note && <p className="mt-4 text-sm">{e.note}</p>}
        <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
          {e.websiteUrl && (
            <>
              <dt className="text-muted uppercase tracking-widest text-[0.7rem] font-(family-name:--font-ui)">Website</dt>
              <dd><a href={e.websiteUrl} target="_blank" rel="noopener" className="text-accent hover:underline break-all">{e.websiteUrl}</a></dd>
            </>
          )}
          {e.linkedInUrl && (
            <>
              <dt className="text-muted uppercase tracking-widest text-[0.7rem] font-(family-name:--font-ui)">LinkedIn</dt>
              <dd><a href={e.linkedInUrl} target="_blank" rel="noopener" className="text-accent hover:underline break-all">{e.linkedInUrl}</a></dd>
            </>
          )}
          {e.rssUrl && (
            <>
              <dt className="text-muted uppercase tracking-widest text-[0.7rem] font-(family-name:--font-ui)">RSS</dt>
              <dd><a href={e.rssUrl} target="_blank" rel="noopener" className="text-accent hover:underline break-all">{e.rssUrl}</a></dd>
            </>
          )}
        </dl>
      </Card>

      <Card>
        <CardTitle className="text-xl">Recent News ({news.data?.length ?? 0})</CardTitle>
        {news.isLoading && <p className="text-muted mt-3">Loading…</p>}
        {news.error && <ErrorState message={(news.error as Error).message} />}
        {news.data && news.data.length === 0 && (
          <CardDescription className="mt-3">
            No news items yet for this entity.
          </CardDescription>
        )}
        {news.data && news.data.length > 0 && (
          <>
            {/* Desktop */}
            <table className="hidden md:table w-full text-sm mt-4">
              <thead>
                <tr className="border-b-2 border-accent">
                  <th style={{ width: 110 }} className="py-2 px-3 text-left text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui)">When</th>
                  <th style={{ width: 160 }} className="py-2 px-3 text-left text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui)">Source</th>
                  <th className="py-2 px-3 text-left text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui)">Headline</th>
                  <th style={{ width: 60 }} className="py-2 px-3 text-center text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui)">Score</th>
                </tr>
              </thead>
              <tbody>
                {news.data.map((n) => <NewsRowDesktop key={n.id} n={n} />)}
              </tbody>
            </table>
            {/* Mobile */}
            <ul className="md:hidden flex flex-col divide-y divide-border mt-2">
              {news.data.map((n) => <NewsRowMobile key={n.id} n={n} />)}
            </ul>
          </>
        )}
      </Card>
    </>
  );
}

function NewsRowDesktop({ n }: { n: NewsItemDto }) {
  return (
    <tr className="border-b border-border align-top hover:bg-surface-hover">
      <td className="py-3 px-3 text-muted text-xs whitespace-nowrap">{fmtLocalDate(n.publishedAt)}</td>
      <td className="py-3 px-3 text-muted text-xs">{n.sourceType}</td>
      <td className="py-3 px-3">
        <a href={n.sourceUrl} target="_blank" rel="noopener" className="text-accent hover:underline">
          {n.headline}
        </a>
        {n.whyItMatters && <div className="text-[0.85rem] mt-1 text-accent">↳ {n.whyItMatters}</div>}
      </td>
      <td className="py-3 px-3 text-center align-middle">
        <ScoreTag score={n.relevanceScore} />
      </td>
    </tr>
  );
}

function NewsRowMobile({ n }: { n: NewsItemDto }) {
  return (
    <li className="py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[0.7rem] text-muted font-(family-name:--font-ui) uppercase tracking-wider mb-1">
            {fmtLocalDate(n.publishedAt)} · {n.sourceType}
          </div>
          <a href={n.sourceUrl} target="_blank" rel="noopener" className="text-accent hover:underline font-medium">
            {n.headline}
          </a>
          {n.whyItMatters && <p className="text-sm mt-1 text-accent">↳ {n.whyItMatters}</p>}
        </div>
        <ScoreTag score={n.relevanceScore} />
      </div>
    </li>
  );
}
